import { Suspense, useEffect, useState } from "react";
import { Loader } from "rsuite";
import { useTranslation } from "react-i18next";

import { Card } from "components/common/card/Card";
import { PaymentActivity } from "components/widgets/paymentActivity/paymentActivity";
import { UpcomingPayments } from "components/widgets/upcomingPayments/UpcomingPayments";
import { ScheduledPayments } from "components/widgets/scheduledPayments/scheduledPayments";
import { ScheduledPaymentsActions } from "components/widgets/scheduledPayments/scheduledPaymentsActions";

import { SavedPaymentMethods } from "components/widgets/savedPaymentMethods/SavedPaymentMethods";
import { AccountOverview } from "components/widgets/accountOverview/AccountOverview";
import LoadTranslation from "components/common/LoadTranslation";
import {
  clearAllNotifications,
  clearNotification,
} from "store/notification/notification-slice";

import { useAddNotification } from "hooks";

import { useRootSelector, useRootDispatch } from "store";
import {
  selectAllAccounts,
  getAccountRestrictions,
  selectedAccountInfo,
} from "store/account/account-selector";

import classes from "./DashboardMain.module.scss";
import { getAccountRestrictionWarning } from "utility/account";
import {
  selectExpiredCardPaymentMethods,
  selectExpiringNextMonthCardPaymentMethods,
} from "store/paymentMethods/paymentMethods-slice";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";

const widgets = [
  "accountOverview",
  "upcomingPayment",
  "scheduledPayments",
  "savedPaymentMethod",
  "paymentActivity",
];

export const DashboardMain = () => {
  const { t } = useTranslation(["common", "payment"]);
  const dispatch = useRootDispatch();
  const addNotification = useAddNotification();

  const allAccounts = useRootSelector(selectAllAccounts);
  const { client } = useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const { restrictions } = useRootSelector(getAccountRestrictions);
  const anyRestriction = Object.values(restrictions).some(
    (value) => value === true,
  );

  const expiredCardPaymentMethods = useRootSelector(
    selectExpiredCardPaymentMethods,
  );
  const expiringNextMonthCardPaymentMethods = useRootSelector(
    selectExpiringNextMonthCardPaymentMethods,
  );

  useEffect(() => {
    dispatch(clearAllNotifications());
    // eslint-disable-next-line
  }, []);

  const [notifiedCards, setNotifiedCards] = useState<number[]>([]);
  const [accountRestrictionNotification, setAccountRestrictionNotification] =
    useState<{
      accountId: number | null;
      notificationId: string | null;
    }>({ accountId: null, notificationId: null });

  useEffect(() => {
    if (
      accountInfo?.id &&
      accountRestrictionNotification.accountId !== accountInfo.id &&
      accountRestrictionNotification.notificationId
    ) {
      dispatch(
        clearNotification(accountRestrictionNotification.notificationId),
      );
      setAccountRestrictionNotification({
        accountId: null,
        notificationId: null,
      });
    }

    if (
      anyRestriction &&
      accountInfo?.id &&
      accountRestrictionNotification.accountId !== accountInfo.id
    ) {
      const id = addNotification({
        translatedMessages: [
          t(getAccountRestrictionWarning(restrictions), {
            account: accountInfo?.accountNumber,
            phone: client.primaryContactPhone,
          }),
        ],
        closable: false,
        type: "DEFAULT",
      });
      setAccountRestrictionNotification({
        accountId: accountInfo.id,
        notificationId: id,
      });
    }
  }, [
    accountInfo,
    anyRestriction,
    restrictions,
    accountRestrictionNotification,
    client,
    addNotification,
    dispatch,
    t,
  ]);

  useEffect(() => {
    const notifyUser = (cardMethods: PaymentMethod[], messageKey: string) => {
      cardMethods.forEach((card) => {
        const cardId = card.id!;
        if (!notifiedCards.includes(cardId)) {
          addNotification({
            translatedMessages: [
              t(messageKey, {
                cardNumber: getCardLastNumbers([card]),
              }),
            ],
            type: "DEFAULT",
            closable: true,
          });
          setNotifiedCards((prev) => [...prev, cardId]);
        }
      });
    };
    notifyUser(expiredCardPaymentMethods, "common:card_expired");
    notifyUser(expiringNextMonthCardPaymentMethods, "common:card_expiring");
  }, [
    expiredCardPaymentMethods,
    expiringNextMonthCardPaymentMethods,
    dispatch,
    addNotification,
    t,
    notifiedCards,
  ]);

  const getCardLastNumbers = (cardPaymentMethods: PaymentMethod[]) => {
    return cardPaymentMethods
      .map((method) => method.lastFour)
      .filter((lastFour): lastFour is string => !!lastFour)
      .join(",");
  };

  const renderComponent = (comp: string) => {
    switch (comp) {
      case "scheduledPayments":
        return (
          <Card
            title={t("common:scheduled_payments")}
            actions={ScheduledPaymentsActions()}
          >
            <ScheduledPayments />
          </Card>
        );
      case "upcomingPayment":
        return (
          <Card title={t("common:upcoming_payments")}>
            <UpcomingPayments />
          </Card>
        );
      case "paymentActivity":
        return (
          <Card title={t("common:payment_activity")}>
            <Suspense
              fallback={
                <section
                  style={{
                    display: "grid",
                    placeItems: "center",
                    height: "150px",
                  }}
                >
                  <Loader />
                </section>
              }
            >
              <PaymentActivity
                widgetView
                showAllButton
                hideControls
                hideHeader
              />
            </Suspense>
          </Card>
        );
      case "accountOverview":
        return (
          <div className="AccountOverview">
            {allAccounts.length > 1 && (
              <Card showDivider={false}>
                <AccountOverview />
              </Card>
            )}
          </div>
        );
      case "savedPaymentMethod":
        return (
          <Card title={t("common:saved_payment_methods")}>
            <SavedPaymentMethods />
          </Card>
        );
      default:
        return null;
    }
  };

  const renderWidget = (widgets: string[]) => {
    return widgets.map((single, i) => {
      return (
        <div
          className={[classes.Widget, classes[single]].join(" ")}
          key={single}
        >
          {renderComponent(single)}
        </div>
      );
    });
  };

  return (
    <>
      <div className={classes.Container}>
        {renderWidget(widgets)}
        <LoadTranslation keys={["payment_next", "account"]} />
      </div>
    </>
  );
};
